<template>
    <div class="w-full">
        <!-- Loader animado: a linha se desenha em loop enquanto os dados chegam -->
        <div
            v-if="loading && animate"
            class="relative h-96 w-full overflow-hidden rounded-lg"
            aria-hidden="true"
        >
            <svg
                viewBox="0 0 800 300"
                preserveAspectRatio="none"
                class="h-full w-full"
                fill="none"
            >
                <line
                    v-for="n in 4"
                    :key="n"
                    x1="0"
                    :y1="n * 60"
                    x2="800"
                    :y2="n * 60"
                    class="animate-pulse stroke-gray-200 dark:stroke-gray-700"
                    stroke-width="1"
                />
                <path
                    :d="loaderPath"
                    class="stroke-gray-200 dark:stroke-gray-700"
                    stroke-width="3"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
                <path
                    :d="loaderPath"
                    class="line-loader-path stroke-teal-500"
                    stroke-width="3"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    pathLength="1"
                />
            </svg>
        </div>
        <chart-skeleton v-else-if="loading" />
        <highcharts
            v-if="!loading && chart"
            :key="dark ? 'dark' : 'light'"
            :constructor-type="'chart'"
            :options="themedChart"
        ></highcharts>
    </div>
</template>

<script>
import ChartSkeleton from "../../../components/layouts/ChartSkeleton.vue";
import { defineComponent } from "vue";
import { Store } from "../../../stores/store";
import { mapWritableState } from "pinia";

export default defineComponent({
    components: {
        ChartSkeleton,
    },
    props: {
        loading: { required: false, type: Boolean, default: false },
        series: { required: true, type: Array, default: () => [] },
        enablePointClick: { required: false, type: Boolean, default: false },
        // Formata o eixo Y como percentual ({value}%)
        percent: { required: false, type: Boolean, default: false },
        // Altura fixa em px; sem ela vale o default do Highcharts (400)
        height: { required: false, type: Number, default: null },
        // Loader com linha se desenhando + draw-in da série ao carregar
        animate: { required: false, type: Boolean, default: false },
    },
    emits: ["point-click"],
    data() {
        return {
            chart: false,
            loaderPath:
                "M0,220 C50,205 80,140 130,150 S220,235 280,210 S370,95 430,115 S540,200 600,170 S700,80 760,100 L800,92",
        };
    },

    computed: {
        ...mapWritableState(Store, ["dark"]),

        // O tema global do Highcharts (App.vue) é sempre escuro; sobrescrevemos
        // aqui para este componente seguir o tema claro/escuro corrente do app.
        themedChart() {
            if (!this.chart) return this.chart;

            const gridColor = this.dark ? "#374151" : "#e5e7eb";
            const textColor = this.dark ? "#9ca3af" : "#6b7280";

            return {
                ...this.chart,
                chart: {
                    ...this.chart.chart,
                    backgroundColor: "transparent",
                    style: { fontFamily: "inherit" },
                },
                xAxis: {
                    ...this.chart.xAxis,
                    gridLineColor: gridColor,
                    lineColor: gridColor,
                    tickColor: gridColor,
                    labels: { style: { color: textColor } },
                },
                yAxis: (Array.isArray(this.chart.yAxis)
                    ? this.chart.yAxis
                    : [this.chart.yAxis]
                ).map((axis) => ({
                    ...axis,
                    gridLineColor: gridColor,
                    labels: {
                        ...axis?.labels,
                        style: { color: textColor },
                    },
                })),
                legend: {
                    ...this.chart.legend,
                    itemStyle: { color: this.dark ? "#e5e7eb" : "#374151" },
                    itemHoverStyle: {
                        color: this.dark ? "#ffffff" : "#111827",
                    },
                },
                tooltip: {
                    ...this.chart.tooltip,
                    backgroundColor: this.dark
                        ? "rgba(17, 24, 39, 0.95)"
                        : "rgba(255, 255, 255, 0.97)",
                    borderColor: gridColor,
                    style: { color: this.dark ? "#f3f4f6" : "#111827" },
                },
                colors: ["#14b8a6", "#6366f1", "#f59e0b", "#ef4444"],
            };
        },
    },

    watch: {
        series: {
            handler(newVal) {
                if (!newVal?.length) return;

                let chart = this.chartObject();
                chart.series = [...newVal]; // Alterado aqui
                this.chart = chart;
            },
            immediate: true,
        },
    },

    methods: {
        chartObject() {
            const chartConfig = {
                tooltip: {
                    formatter: function () {
                        let chart = this;

                        let date = new Date(chart.x);
                        let formattedDate = date.toLocaleDateString("pt-BR", {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                        });

                        return `<b>${formattedDate}</b><br />${chart.points
                            .sort((pointA, pointB) => pointB.y - pointA.y)
                            .map((point) => {
                                return (
                                    `<span style="color: ${point.color}">\u25CF</span> ${point.series.name}: ` +
                                    point.y.toLocaleString("pt-br")
                                );
                            })
                            .join("<br />")}`;
                    },
                    xDateFormat: "%d/%m/%Y",
                    shared: true,
                    split: false,
                    enabled: true,
                },

                credits: {
                    enabled: false,
                },

                legend: {
                    enabled: true,
                },

                title: {
                    align: "left",
                    text: null,
                },

                labels: {
                    format: "{value:%l:%M %p }",
                },

                xAxis: {
                    type: "datetime",
                    dateTimeLabelFormats: {
                        day: "%d/%m",
                        week: "%d/%m",
                        month: "%m/%Y",
                    },
                    tickPixelInterval: 100,
                    maxPadding: 0.02,
                    minPadding: 0.02,
                },

                yAxis: {
                    title: null,
                    ...(this.percent && {
                        labels: { format: "{value}%" },
                    }),
                },

                series: [],
            };

            if (this.height) {
                chartConfig.chart = { height: this.height };
            }

            if (this.enablePointClick) {
                chartConfig.plotOptions = {
                    series: {
                        cursor: "pointer",
                        marker: {
                            enabled: false, // Oculto por padrão
                            radius: 4,
                            lineWidth: 1,
                            lineColor: "#ffffff",
                        },
                        point: {
                            events: {
                                click: (event) => {
                                    this.$emit("point-click", {
                                        x: event.point.x,
                                        y: event.point.y,
                                        pointData:
                                            event.point.options.pointData,
                                    });
                                },
                            },
                        },
                        states: {
                            hover: {
                                marker: {
                                    enabled: true, // Aparece só no hover
                                    radius: 6,
                                    lineWidth: 2,
                                },
                            },
                        },
                    },
                };
            }

            if (this.animate) {
                chartConfig.plotOptions = chartConfig.plotOptions || {};
                chartConfig.plotOptions.series = {
                    ...(chartConfig.plotOptions.series || {}),
                    animation: { duration: 1600 },
                };
            }

            return chartConfig;
        },
    },
});
</script>

<style scoped>
.line-loader-path {
    stroke-dasharray: 1;
    stroke-dashoffset: 1;
    animation: line-loader-draw 1.8s cubic-bezier(0.4, 0, 0.2, 1) infinite;
}

@keyframes line-loader-draw {
    0% {
        stroke-dashoffset: 1;
        opacity: 1;
    }
    65% {
        stroke-dashoffset: 0;
        opacity: 1;
    }
    88% {
        stroke-dashoffset: 0;
        opacity: 0;
    }
    100% {
        stroke-dashoffset: 1;
        opacity: 0;
    }
}

@media (prefers-reduced-motion: reduce) {
    .line-loader-path {
        animation: none;
        stroke-dashoffset: 0;
        opacity: 1;
    }
}
</style>
